import { useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  getLocalePreference,
  isSupportedLocale,
  setLocalePreference,
  type SupportedLocale,
} from "@app/i18n";
import { apiClient } from "@/lib/ApiClient";
import {
  getAppearancePreference,
  getThemePalette,
  isAppearancePreference,
  isThemePalette,
  setAppearancePreference,
  setThemePalette,
  type AppearancePreference,
  type ThemePalette,
} from "@/lib/Theme";

// 사이드바 팀 순서 쿼리(useTeamOrderQuery 등)는 IndexedDB(dexie) 를 쓰므로 이 파일에서
// ./TeamOrder.ts 로 분리했다 — App.tsx 가 첫 화면에서 이 파일(테마/언어 서버 동기화)을
// 정적 import 할 때 dexie 가 entry(초기) 청크로 딸려오지 않게 하기 위함이다.
// userPreferenceKeys 도 TeamOrder.ts 로 옮겼다.

/**
 * 세션 확립 후 1회, 서버에 저장된 테마 선호값(팔레트 + 밝기 모드)과 로컬
 * (localStorage)을 수렴시킨다. 모든 테마 변경이 즉시 PUT되므로 서버가 항상
 * 최신 값 = 서버 우선(last-write-wins). 서버 값이 비어 있으면(미설정) 현재
 * 로컬 값을 올려 시드한다. 실패는 조용히 무시(오프라인 등 — 로컬 값으로 계속
 * 동작하고 다음 부팅 때 재시도).
 */
export function useSyncThemeFromServer(enabled: boolean) {
  const syncedRef = useRef(false);

  useEffect(() => {
    // 세션이 없거나 이미 이번 부팅에 동기화했는지
    const shouldSkipSync = !enabled || syncedRef.current;
    if (shouldSkipSync) return;
    syncedRef.current = true;
    void (async () => {
      try {
        const res = await apiClient.api.preferences.theme.$get();
        // 테마 조회 응답이 실패했는지
        const isResNotOk = !res.ok;
        if (isResNotOk) return;
        const body = (await res.json()) as {
          theme: ThemePalette | null;
          appearance: AppearancePreference | null;
        };
        // 유효하지 않은 값(과거 포맷 등)은 미설정으로 취급
        const serverPalette = isThemePalette(body.theme) ? body.theme : null;
        const serverAppearance = isAppearancePreference(body.appearance)
          ? body.appearance
          : null;
        const localPalette = getThemePalette();
        const localAppearance = getAppearancePreference();
        // 서버에 유효한 값이 하나라도 비어 있는지 (최초 동기화 → 로컬 값으로 채워 시드)
        const isServerIncomplete = !serverPalette || !serverAppearance;
        if (isServerIncomplete) {
          await apiClient.api.preferences.theme.$put({
            json: {
              theme: serverPalette ?? localPalette,
              appearance: serverAppearance ?? localAppearance,
            },
          });
        }
        // 다른 기기에서 바꾼 값인지 (로컬과 다른지)
        const isPaletteNewer = !!serverPalette && serverPalette !== localPalette;
        if (isPaletteNewer) setThemePalette(serverPalette);
        const isAppearanceNewer =
          !!serverAppearance && serverAppearance !== localAppearance;
        if (isAppearanceNewer) setAppearancePreference(serverAppearance);
      } catch {
        // 네트워크 오류 — 로컬 값 유지
      }
    })();
  }, [enabled]);
}

/**
 * 세션 확립 후 1회, 서버에 저장된 언어 선호값과 로컬(localStorage)을 수렴시킨다.
 * useSyncThemeFromServer 와 동일한 구조 — 서버 우선(last-write-wins), 서버가
 * 비어 있으면 현재 로컬 값으로 시드, 실패는 조용히 무시.
 */
export function useSyncLocaleFromServer(enabled: boolean) {
  const syncedRef = useRef(false);

  useEffect(() => {
    // 세션이 없거나 이미 이번 부팅에 동기화했는지
    const shouldSkipSync = !enabled || syncedRef.current;
    if (shouldSkipSync) return;
    syncedRef.current = true;
    void (async () => {
      try {
        const res = await apiClient.api.preferences.locale.$get();
        // 언어 조회 응답이 실패했는지
        const isResNotOk = !res.ok;
        if (isResNotOk) return;
        const body = (await res.json()) as { locale: SupportedLocale | null };
        // 유효하지 않은 값(과거 포맷 등)은 미설정으로 취급
        const serverLocale = isSupportedLocale(body.locale) ? body.locale : null;
        const localLocale = getLocalePreference();
        // 서버에 값이 없는지 (최초 동기화 → 로컬 값으로 채워 시드)
        const isServerEmpty = !serverLocale;
        if (isServerEmpty) {
          await apiClient.api.preferences.locale.$put({ json: { locale: localLocale } });
          return;
        }
        // 다른 기기에서 바꾼 값인지 (로컬과 다른지)
        const isLocaleNewer = serverLocale !== localLocale;
        if (isLocaleNewer) setLocalePreference(serverLocale);
      } catch {
        // 네트워크 오류 — 로컬 값 유지
      }
    })();
  }, [enabled]);
}

/** 언어 선호값을 서버에 저장한다. 로컬 적용은 호출부 책임. */
export function useSaveLocaleMutation() {
  return useMutation({
    mutationFn: async (locale: SupportedLocale) => {
      const res = await apiClient.api.preferences.locale.$put({ json: { locale } });
      // 언어 저장 응답이 실패했는지
      const isResNotOk = !res.ok;
      if (isResNotOk) throw new Error("failed to save locale");
      return locale;
    },
  });
}

/** 테마 선호값(팔레트 + 밝기 모드)을 서버에 저장한다. 로컬 적용은 호출부 책임. */
export function useSaveThemeMutation() {
  return useMutation({
    mutationFn: async (prefs: { theme: ThemePalette; appearance: AppearancePreference }) => {
      const res = await apiClient.api.preferences.theme.$put({ json: prefs });
      // 테마 저장 응답이 실패했는지
      const isResNotOk = !res.ok;
      if (isResNotOk) throw new Error("failed to save theme");
      return prefs;
    },
  });
}
